import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  PayMonthNumberOfReportedViewModel,
  PayMonthOverviewViewModel,
  PayMonthStatusEnum,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Assets, Box, Flex, Headings, Link, Spacer, Text, WideBox } from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../i18n/i18n';
import { getNumberOfReportedForPayMonth } from '../../redux/payroll/payrollThunks';
import { useAppDispatch } from '../../redux/hooks';
import { HistoryLogReports } from '../HistoryLog';

type PayReportsTableRowExpandedProps = {
  payMonth: PayMonthOverviewViewModel;
  rowTitle: string;
  manualReportingLink: string;
  isAnyReportProcessing: boolean;
};

export const PayReportsTableRowExpanded = ({
  payMonth,
  rowTitle,
  manualReportingLink,
  isAnyReportProcessing,
}: PayReportsTableRowExpandedProps) => {
  const filteredReports = useMemo(
    () => payMonth.payrollReportHeads.filter(report => report.payMonths.some(pm => pm.payMonth === payMonth.payMonth)),
    [payMonth.payMonth, payMonth.payrollReportHeads]
  );

  if (payMonth.payMonthStatus === PayMonthStatusEnum.ReportingNotDone) {
    return (
      <Box maxWidth={578} marginRight="auto" marginLeft="auto">
        <ReportingStatusIngress reportingPeriod={payMonth} rowTitle={rowTitle} />
        <ReportActionLinks manualReportingLink={manualReportingLink} payMonth={payMonth.payMonth} />
      </Box>
    );
  } else {
    return (
      <Box maxWidth={578} marginRight="auto" marginLeft="auto">
        <ReportingStatusIngress reportingPeriod={payMonth} rowTitle={rowTitle} />
        {!isAnyReportProcessing && <PayReportOverviewBox payMonth={payMonth} />}
        <ReportActionLinks manualReportingLink={manualReportingLink} payMonth={payMonth.payMonth} />
        {filteredReports.length > 0 && (
          <Spacer marginTop={40}>
            <HistoryLogReports reports={filteredReports} />
          </Spacer>
        )}
      </Box>
    );
  }
};

const ReportingStatusIngress = ({
  reportingPeriod,
  rowTitle,
}: {
  reportingPeriod: PayMonthOverviewViewModel;
  rowTitle: string;
}) => {
  const { t } = useTypedTranslation();
  return (
    <Box marginBottom={40}>
      <Headings.H4 marginBottom={6}>{rowTitle}</Headings.H4>
      {reportingPeriod.payMonthStatus === PayMonthStatusEnum.ReportingNotDone && (
        <Text size="standard">
          {t('status.reportingNotDone.lastDateToReport', {
            lastDateToReport: reportingPeriod.periodToDate,
          })}
        </Text>
      )}
      {reportingPeriod.payMonthStatus === PayMonthStatusEnum.ReportProcessing && (
        <Text>{t('status.reportProcessing.description')}</Text>
      )}
      {reportingPeriod.payMonthStatus === PayMonthStatusEnum.ReportWithDeviations && (
        <Text>{t('status.reportWithDeviations.description')}</Text>
      )}
      {reportingPeriod.payMonthStatus === PayMonthStatusEnum.NoActiveEmployments && (
        <Text>{t('status.noActiveEmployments.description')}</Text>
      )}
      {reportingPeriod.payMonthStatus === PayMonthStatusEnum.ReportingDelayed && (
        <Text>{t('status.reportingDelayed.description', { endDate: reportingPeriod.periodToDate })}</Text>
      )}
    </Box>
  );
};

const PayReportOverviewBox = ({ payMonth }: { payMonth: PayMonthOverviewViewModel }) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const [numberOfReported, setNumberOfReported] = useState<PayMonthNumberOfReportedViewModel | null>();
  const [loading, setLoading] = useState(true);

  const getNumberOfReportedByPayMonth = useCallback(
    async (payMonth: string) => {
      const result = await dispatch(getNumberOfReportedForPayMonth(payMonth));
      if (getNumberOfReportedForPayMonth.fulfilled.match(result) && result.payload) {
        return result.payload;
      }
      return Promise.reject(`Could not get number of reported for ${payMonth}`);
    },
    [dispatch]
  );

  useEffect(() => {
    getNumberOfReportedByPayMonth(payMonth.payMonth)
      .then(numberOfReported => {
        setNumberOfReported(numberOfReported);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [getNumberOfReportedByPayMonth, payMonth.payMonth]);

  return (
    <WideBox marginBottom={40}>
      <Flex flexWrap="wrap" rowGap={20}>
        {loading ? (
          <Assets.Loading width={40} marginLeft="auto" marginRight="auto" color="black20" />
        ) : !numberOfReported ? (
          <Text>{t('errorMessages.validation.payroll.couldNotFetchReportedPayrolls')}</Text>
        ) : (
          <>
            <Box flexGrow={1} flexBasis={250}>
              <Text size="xs" color="black70">
                {t('section.reportDetails.numberOfActiveEmploymentsTitle')}
              </Text>
              <Headings.H3 marginTop={6}>
                {t('section.reportDetails.activeEmploymentAmount', {
                  activeEmploymentAmount: numberOfReported.numberOfActiveEmployments,
                })}
              </Headings.H3>
            </Box>
            <Box flexGrow={1} flexBasis={250}>
              <Text size="xs" color="black70">
                {t('section.reportDetails.numberOfReportedPayrollsTitle')}
              </Text>
              <Headings.H3 marginTop={6}>
                {t('section.reportDetails.reportedPayrollsAmount', {
                  reportedPayrollsAmount: numberOfReported.numberOfReportedPayrollsOnActiveEmployments,
                })}
              </Headings.H3>
            </Box>
          </>
        )}
      </Flex>
    </WideBox>
  );
};

const ReportActionLinks = ({ manualReportingLink, payMonth }: { manualReportingLink: string; payMonth: string }) => {
  const { t } = useTypedTranslation();
  return (
    <Flex flexWrap="wrap" columnGap={1} rowGap={1}>
      <Link to={manualReportingLink} styleAs="button" hasIcon={false} testId={`test-id-manual-report-${payMonth}`}>
        <Assets.HandPointer marginRight={10} containerSize={14} width={14} />
        {t('section.reportDetails.manualReportButton')}
      </Link>
    </Flex>
  );
};
